import { create } from 'zustand';

/**
 * Theme runtime and the white-label entry point.
 *
 * The palette itself lives in `brand.css` as CSS variables; nothing in TS holds
 * a colour. This module only decides *which* theme is active and, for tenants
 * whose brand arrives at runtime rather than at build time, lets those
 * variables be overwritten on `<html>` — see `applyBrandTokens`.
 */

export type Theme = 'light' | 'dark';

const THEME_KEY = 'quickbee_theme';

/**
 * The stored choice, or the OS preference when the saver has never chosen.
 *
 * Falling back to the OS reading rather than hard-defaulting to light means a
 * phone in dark mode opens dark on first launch, which is what people expect;
 * once they touch the toggle their choice wins forever after.
 *
 * Kept in sync with the pre-paint script in index.html — same key, same rule.
 */
export function resolveInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Private mode / storage disabled — fall through to the OS reading.
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * The whole palette hangs off one class on `<html>`: every colour in the app is
 * an `oklch(var(--token))`, and `.dark` redeclares those tokens. Nothing else
 * has to know the theme changed.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.style.colorScheme = theme;
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: resolveInitialTheme(),
  setTheme: (theme) => {
    applyTheme(theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // A theme that does not survive a reload still beats a crash.
    }
    set({ theme });
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark')
}));

/* ------------------------------------------------------------------ */
/* White-label                                                         */
/* ------------------------------------------------------------------ */

/** The scales a tenant may re-skin. Mirrors the groups in `brand.css`. */
export const BRAND_SCALES = [
  'background',
  'foreground',
  'primary',
  'accent',
  'secondary'
] as const;

export type BrandScale = (typeof BRAND_SCALES)[number];

/** Step keys per scale, and the three fixed `ink` steps. */
export type ScaleStep = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;
export type InkStep = 700 | 800 | 900;

/**
 * An OKLCH triple as `brand.css` writes it: `"0.42 0.16 18"` — lightness,
 * chroma, hue, unwrapped. Not a CSS colour: Tailwind wraps it so that the
 * `/<alpha-value>` opacity modifiers keep working.
 */
export type OklchTriple = string;

export interface BrandTokens {
  /** Per-theme scale overrides. Omit a theme to leave `brand.css` in charge. */
  light?: Partial<Record<BrandScale, Partial<Record<ScaleStep, OklchTriple>>>>;
  dark?: Partial<Record<BrandScale, Partial<Record<ScaleStep, OklchTriple>>>>;
  /** Surfaces that stay dark in both themes; not theme-scoped. */
  ink?: Partial<Record<InkStep, OklchTriple>>;
  fonts?: { heading?: string; body?: string; label?: string };
}

/** Where runtime overrides are written, so they can be cleanly removed. */
const OVERRIDE_STYLE_ID = 'qb-brand-override';

function scaleRules(
  tokens: NonNullable<BrandTokens['light']>,
  indent: string
): string {
  const out: string[] = [];
  for (const scale of Object.keys(tokens) as BrandScale[]) {
    const steps = tokens[scale];
    if (!steps) continue;
    for (const [step, value] of Object.entries(steps)) {
      if (value) out.push(`${indent}--${scale}-${step}: ${value};`);
    }
  }
  return out.join('\n');
}

/**
 * Apply a tenant palette at runtime, no rebuild.
 *
 * Build-time white-labelling is the simpler path — fork `brand.css` and ship
 * it. This exists for the case where one deployment serves many tenants and
 * the palette only becomes known once the host has been resolved: call it
 * during bootstrap, right after `initTenant()`, before the first paint.
 *
 * Overrides are written as a stylesheet rather than inline styles so that
 * `.dark` still wins over `:root` in the normal cascade — inline styles on
 * `<html>` would apply to both themes at once and break the toggle.
 */
export function applyBrandTokens(tokens: BrandTokens): string | null {
  const css = buildBrandCss(tokens);
  if (!css) return null;

  let el = document.getElementById(OVERRIDE_STYLE_ID);
  if (!el) {
    el = document.createElement('style');
    el.id = OVERRIDE_STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = css;
  return css;
}

/**
 * The same stylesheet as a string, without touching the document.
 *
 * Exported because the palette has to reach the page twice, from two places that
 * share nothing: here, once the host's branding has been fetched, and — replayed
 * verbatim from `localStorage` — inside the blocking brand script at the top of
 * `index.html`, which runs before the bundle exists. Caching the generated TEXT is
 * what keeps that script from carrying a second copy of the ladder maths.
 */
export function buildBrandCss(tokens: BrandTokens): string | null {
  const blocks: string[] = [];

  if (tokens.light || tokens.fonts) {
    const rules = [
      tokens.light ? scaleRules(tokens.light, '  ') : '',
      tokens.ink
        ? Object.entries(tokens.ink)
            .filter(([, v]) => v)
            .map(([step, v]) => `  --ink-${step}: ${v};`)
            .join('\n')
        : '',
      tokens.fonts?.heading ? `  --font-heading: ${tokens.fonts.heading};` : '',
      tokens.fonts?.body ? `  --font-body: ${tokens.fonts.body};` : '',
      tokens.fonts?.label ? `  --font-label: ${tokens.fonts.label};` : ''
    ]
      .filter(Boolean)
      .join('\n');
    if (rules) blocks.push(`:root {\n${rules}\n}`);
  }

  if (tokens.dark) {
    const rules = scaleRules(tokens.dark, '  ');
    if (rules) blocks.push(`.dark {\n${rules}\n}`);
  }

  return blocks.length ? blocks.join('\n\n') : null;
}

/** Drop a runtime override and fall back to `brand.css`. */
export function clearBrandTokens(): void {
  document.getElementById(OVERRIDE_STYLE_ID)?.remove();
}

/* ------------------------------------------------------------------ */
/* Dev-time wiring check                                               */
/* ------------------------------------------------------------------ */

/**
 * Fail loudly when the theme is only half-wired.
 *
 * The failure this exists for: `tailwind.config.ts` gains a colour, the running
 * dev server does not restart, and the new utility class is simply absent from
 * the stylesheet. Nothing errors — the elements using it render transparent, so
 * the tab bar and the balance hero silently disappear against the page. That is
 * a confusing five minutes; this turns it into one console line.
 *
 * Probes the utility class, not the variable, because the variable resolving is
 * exactly what makes the failure look impossible.
 */
export function assertThemeWiring(): void {
  if (!import.meta.env.DEV) return;

  /** Classes whose absence blanks a whole surface rather than tinting it. */
  const REQUIRED = ['bg-ink-900', 'bg-primary-500', 'bg-background-50'];

  const unresolved = (): string[] => {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;opacity:0;pointer-events:none';
    document.body.appendChild(probe);
    const broken = REQUIRED.filter((cls) => {
      probe.className = cls;
      const bg = getComputedStyle(probe).backgroundColor;
      return !bg || bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent';
    });
    probe.remove();
    return broken;
  };

  // Vite injects dev CSS from a JS module, so on the very first frames the
  // stylesheet legitimately is not there yet and every class reads as
  // unresolved. Retry across a few frames before believing it: a guard that
  // cries wolf on every cold start is worse than no guard at all.
  let attempt = 0;
  const MAX_ATTEMPTS = 30;

  const check = () => {
    const broken = unresolved();
    if (!broken.length) return;
    if (++attempt < MAX_ATTEMPTS) {
      requestAnimationFrame(check);
      return;
    }
    console.error(
      `[theme] These utility classes resolve to nothing: ${broken.join(', ')}.\n` +
        'The colour is missing from the generated stylesheet. Two usual causes:\n' +
        '  1. tailwind.config.ts changed and the dev server has not picked it ' +
        'up — restart it (vite --force) and reload.\n' +
        '  2. an @import landed below the @tailwind directives in index.css, ' +
        'which CSS discards silently.'
    );
  };

  requestAnimationFrame(check);
}
